import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Lock, Loader2, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [sessionExpired, setSessionExpired] = useState(() => Boolean(location.state?.sessionExpired));

  // Clear router state so a page reload doesn't re-show the banner.
  useEffect(() => {
    if (location.state?.sessionExpired) {
      setSessionExpired(true);
      navigate("/admin/login", { replace: true, state: null });
    }
  }, [location.state, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      navigate("/admin");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F4EC] flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="max-w-sm w-full bg-[#FFFDF9] rounded-2xl border border-[rgba(58,42,37,0.08)] shadow-[0_20px_50px_rgba(58,42,37,0.08)] p-10">
        <p className="font-serif-display text-2xl tracking-[0.25em] text-[#3A2A25] text-center">CELUSTA</p>
        <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#C9A45C] text-center mt-2 mb-8">Admin Dashboard</p>
        {sessionExpired && (
          <div className="mb-6 flex items-start gap-2.5 p-4 rounded-xl bg-[#FFF6E5] border border-[#C9A45C]/30 text-xs font-light text-[#3A2A25]" data-testid="session-expired-message">
            <Clock size={14} className="text-[#C9A45C] mt-0.5 shrink-0" />
            Your session has expired. Please sign in again to continue.
          </div>
        )}
        <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            required
            className="w-full bg-[#F8F4EC] border border-[rgba(58,42,37,0.15)] rounded-lg px-4 py-3 text-sm text-[#2B1F1B] placeholder:text-[#9A8D85] focus:outline-none focus:border-[#3A2A25] transition-colors duration-300"
            data-testid="admin-email-input"
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            required
            className="w-full bg-[#F8F4EC] border border-[rgba(58,42,37,0.15)] rounded-lg px-4 py-3 text-sm text-[#2B1F1B] placeholder:text-[#9A8D85] focus:outline-none focus:border-[#3A2A25] transition-colors duration-300"
            data-testid="admin-password-input"
          />
          {error && <p className="text-xs text-[#C62828]" data-testid="admin-login-error">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="w-full px-8 py-3.5 bg-[#3A2A25] text-white text-xs font-semibold uppercase tracking-[0.2em] rounded-full hover:bg-[#55423A] transition-colors duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="admin-login-button"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />}
            Sign In
          </button>
        </form>
        <a href="/admin/forgot-password" className="block text-center mt-6 text-xs text-[#C9A45C] hover:underline" data-testid="forgot-password-link">
          Forgot password?
        </a>
      </div>
    </div>
  );
}
